import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { fetchTournaments, fetchUsers, fetchDashboardStats } from '../../components/admin/adminApiService';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const [stats, setStats] = useState([
    {
      title: 'Total Users',
      value: '...',
      change: '...',
      changeType: 'neutral',
      icon: '👥'
    },
    {
      title: 'Active Tournaments',
      value: '...',
      change: '...',
      changeType: 'neutral',
      icon: '🏆'
    },
    {
      title: 'Total Revenue',
      value: '...',
      change: '...',
      changeType: 'neutral',
      icon: '💰'
    },
    {
      title: 'Pending Transactions',
      value: '...',
      change: '...',
      changeType: 'neutral',
      icon: '💳'
    }
  ]);

  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        
        // Load tournaments and users to calculate stats
        const [tournamentsResponse, usersResponse] = await Promise.allSettled([
          fetchTournaments(),
          fetchUsers()
        ]);
        
        let totalUsers = 0;
        let activeTournaments = 0;
        
        if (usersResponse.status === 'fulfilled') {
          totalUsers = usersResponse.value.length || 0;
        }
        
        if (tournamentsResponse.status === 'fulfilled') {
          const tournaments = tournamentsResponse.value || [];
          activeTournaments = tournaments.filter(t => t.status === 'ACTIVE').length;
        }
        
        // Update stats with real data
        setStats([
          {
            title: 'Total Users',
            value: totalUsers.toString(),
            change: '+12%',
            changeType: 'positive',
            icon: '👥'
          },
          {
            title: 'Active Tournaments',
            value: activeTournaments.toString(),
            change: activeTournaments > 0 ? '+' + activeTournaments : '0',
            changeType: activeTournaments > 0 ? 'positive' : 'neutral',
            icon: '🏆'
          },
          {
            title: 'Total Revenue',
            value: '$45,230', // This would need a separate API endpoint
            change: '+8.2%',
            changeType: 'positive',
            icon: '💰'
          },
          {
            title: 'Pending Transactions',
            value: '156', // This would need a separate API endpoint
            change: '-5',
            changeType: 'negative',
            icon: '💳'
          }
        ]);
        
        // Generate recent activities from tournaments
        const recentTournaments = tournamentsResponse.value || [];
        const activities = recentTournaments
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5)
          .map((tournament, index) => ({
            id: tournament.id,
            type: 'tournament_created',
            message: `Tournament "${tournament.name}" ${tournament.status === 'ACTIVE' ? 'is active' : 'was created'}`,
            time: new Date(tournament.createdAt).toLocaleDateString()
          }));
          
        setRecentActivities(activities);
        
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-dashboard">
          <div className="admin-card">
            <div className="loading-container">
              <div className="admin-loading-spinner">
                <div className="admin-spinner"></div>
              </div>
              <p>Loading dashboard data...</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-dashboard">
        <div className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">Admin Dashboard</h2>
            <p className="admin-card-subtitle">Welcome to the PROSKINS admin panel</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="admin-stats">
          {stats.map((stat, index) => (
            <div key={index} className="admin-stat-card">
              <div className="admin-stat-icon">{stat.icon}</div>
              <div className="admin-stat-value">{stat.value}</div>
              <div className="admin-stat-label">{stat.title}</div>
              <div className={`admin-stat-change ${stat.changeType}`}>
                {stat.change}
              </div>
            </div>
          ))}
        </div>

        {/* Recent Activities */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Recent Activities</h3>
            <p className="admin-card-subtitle">Latest system activities</p>
          </div>
          <div className="admin-activities">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="admin-activity-item">
                <div className="admin-activity-content">
                  <div className="admin-activity-message">{activity.message}</div>
                  <div className="admin-activity-time">{activity.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Quick Actions</h3>
            <p className="admin-card-subtitle">Common administrative tasks</p>
          </div>
          <div className="admin-quick-actions">
            <button className="admin-btn admin-btn-primary">
              📊 Generate Report
            </button>
            <button className="admin-btn admin-btn-secondary">
              👥 Manage Users
            </button>
            <button className="admin-btn admin-btn-secondary">
              🏆 Create Tournament
            </button>
            <button className="admin-btn admin-btn-secondary">
              ⚙️ System Settings
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
